import React from 'react'
import Modal from '../../common/modals/Modal'
import { PersonDto } from '../../../api/person/models/PersonDto'
import {
    getProfilePicUrl,
    formatText,
    formatPersonName,
} from '../../../utils/personUtils'
import ExperienceTimeline from './ExperienceTimeline'
import ExpandableText from '../../common/expandable/ExpandableText'

interface Props {
    isOpen: boolean
    onClose: () => void
    person: PersonDto
    // Pre-rendered connections section to keep modal dumb/DRY
    connectionsContent?: React.ReactNode
}

export default function PersonProfileModal({
    isOpen,
    onClose,
    person,
    connectionsContent,
}: Props): React.JSX.Element | null {
    if (!isOpen) return null

    const workHistory = person.workedAts ?? []

    return (
        <Modal title=" " onClose={onClose} className="max-h-[calc(100vh-2rem)]">
            {/* Header */}
            <div className="flex gap-4 items-start pb-4 border-b">
                <img
                    src={getProfilePicUrl(person, true)}
                    alt={formatPersonName(person)}
                    className="w-24 h-24 rounded-full object-cover"
                    onError={(e) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = '/no-profile-pic.svg'
                    }}
                />
                <div className="flex-1 min-w-0">
                    <div className="text-xl font-semibold line-clamp-2">
                        {formatPersonName(person)}
                    </div>
                    {person.headline && (
                        <div className="text-gray-700 mt-1 line-clamp-3">
                            {person.headline}
                        </div>
                    )}
                    {person.location && (
                        <div className="text-gray-600 mt-1 text-sm">
                            {person.location}
                        </div>
                    )}
                </div>
            </div>

            {/* Connections (injected) */}
            {connectionsContent ? (
                <section className="mt-4">{connectionsContent}</section>
            ) : null}

            {/* About */}
            {person.summary && (
                <section className="mt-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                        About
                    </h3>
                    <div className="rounded-lg border border-gray-200 p-3 bg-white">
                        <ExpandableText
                            maxLines={5}
                            className="text-sm whitespace-pre-line text-gray-700"
                        >
                            {formatText(person.summary)}
                        </ExpandableText>
                    </div>
                </section>
            )}

            {/* Experience Timeline */}
            {workHistory.length > 0 && (
                <section className="mt-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                        Experience
                    </h3>
                    <div className="rounded-lg border border-gray-200 p-3 bg-white">
                        <ExperienceTimeline jobs={workHistory} />
                    </div>
                </section>
            )}
        </Modal>
    )
}
